import React, { useState } from 'react';
import { Clock, Edit3, Trash2, BarChart3, DollarSign, CheckCircle, AlertCircle } from 'lucide-react';
import API from '../api/axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const SurveyCard = ({ survey, isAdmin, groupId, onRefresh, currentUserId }) => {
  const [voting, setVoting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDeadline, setEditDeadline] = useState('');
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const isExpired = new Date() > new Date(survey.deadline);
  const userVotedOption = survey.options?.find(opt =>
    opt.voters?.some(v => v === currentUserId || v?._id === currentUserId)
  );

  const handleVote = async (optionId) => {
    if (isExpired) { toast.error('انتهى وقت الاستطلاع'); return; }
    setVoting(true);
    try {
      const { data } = await API.post(`/surveys/${survey._id}/vote`, { optionId });
      toast.success(data.message);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في التصويت');
    } finally { setVoting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('هل تريد حذف هذا الاستطلاع؟')) return;
    setDeleting(true);
    try {
      await API.delete(`/surveys/${survey._id}`);
      toast.success('تم الحذف');
      onRefresh();
    } catch { toast.error('فشل الحذف'); } finally { setDeleting(false); }
  };

  const handleEditDeadline = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/surveys/${survey._id}`, { deadline: editDeadline });
      toast.success('تم تحديث الموعد ✅');
      setEditing(false);
      onRefresh();
    } catch { toast.error('فشل التحديث'); }
  };

  const handleShowResults = async () => {
    if (showResults) { setShowResults(false); return; }
    try {
      const { data } = await API.get(`/surveys/${survey._id}/results`);
      setResults(data);
      setShowResults(true);
    } catch { toast.error('خطأ في جلب النتائج'); }
  };

  const totalVoters = survey.options?.reduce((acc, o) => acc + (o.voters?.length || 0), 0);

  return (
    <div className="card animate-slideUp">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span className={`badge ${survey.type === 'payment' ? 'badge-warning' : 'badge-primary'}`}>
              {survey.type === 'payment' ? <><DollarSign size={11} /> مالي</> : <><BarChart3 size={11} /> تقليدي</>}
            </span>
            {isExpired ? (
              <span className="badge badge-danger"><AlertCircle size={11} /> انتهى</span>
            ) : (
              <span className="badge badge-success"><CheckCircle size={11} /> نشط</span>
            )}
          </div>
          <h3 style={{ fontWeight: 700, fontSize: '1rem' }}>{survey.title}</h3>
          {survey.details && <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>{survey.details}</p>}
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginRight: '8px' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(!editing)} style={{ padding: '4px 8px' }}>
              <Edit3 size={14} />
            </button>
            <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting} style={{ padding: '4px 8px' }}>
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Deadline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', color: isExpired ? 'var(--danger)' : 'var(--text-muted)', fontSize: '0.8rem' }}>
        <Clock size={13} />
        <span>
          {isExpired ? 'انتهى في: ' : 'ينتهي في: '}
          {format(new Date(survey.deadline), 'yyyy/MM/dd - HH:mm')}
        </span>
      </div>

      {/* Edit Deadline */}
      {editing && isAdmin && (
        <form onSubmit={handleEditDeadline} style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)' }}>
          <label className="form-label" style={{ marginBottom: '6px', display: 'block' }}>تغيير موعد الانتهاء</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input type="datetime-local" className="form-input"
              value={editDeadline} onChange={e => setEditDeadline(e.target.value)} dir="ltr" />
            <button type="submit" className="btn btn-primary btn-sm">حفظ</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>إلغاء</button>
          </div>
        </form>
      )}

      {/* Options */}
      <div>
        {survey.options?.map((option) => {
          const isSelected = userVotedOption?._id === option._id;
          const voterCount = option.voters?.length || 0;
          const pct = totalVoters > 0 ? Math.round((voterCount / totalVoters) * 100) : 0;

          return (
            <div
              key={option._id}
              className={`survey-option ${isSelected ? 'selected' : ''} ${survey.type === 'payment' ? '' : ''}`}
              onClick={() => !isExpired && !voting && handleVote(option._id)}
              style={{ cursor: isExpired ? 'default' : 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                {/* Radio indicator */}
                <div className={`survey-radio`} style={{
                  borderColor: isSelected ? 'var(--primary)' : 'var(--border-light)',
                  background: isSelected ? 'var(--primary)' : 'transparent'
                }}>
                  {isSelected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white' }} />}
                </div>

                {/* Option Image (payment type) */}
                {survey.type === 'payment' && option.image && (
                  <img src={option.image} alt={option.name}
                    style={{ width: '48px', height: '48px', borderRadius: 'var(--radius-sm)', objectFit: 'cover' }} />
                )}

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{option.name}</span>
                    {survey.type === 'payment' && option.price && (
                      <span className="badge badge-warning">{option.price} ج.م</span>
                    )}
                  </div>

                  {/* Progress bar (show votes) */}
                  {(isExpired || isAdmin || userVotedOption) && (
                    <div style={{ marginTop: '6px' }}>
                      <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '2px', display: 'block' }}>
                        {voterCount} صوت ({pct}%)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          إجمالي الأصوات: {totalVoters}
        </span>

        {isAdmin && (
          <button className="btn btn-secondary btn-sm" onClick={handleShowResults}>
            <BarChart3 size={13} /> {showResults ? 'إخفاء النتائج' : 'النتائج التفصيلية'}
          </button>
        )}
      </div>

      {/* Admin Results */}
      {showResults && results && (
        <div style={{ marginTop: '16px', padding: '16px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)' }}>
          <h4 style={{ marginBottom: '12px', fontSize: '0.9rem', fontWeight: 700 }}>
            📊 النتائج التفصيلية
            {results.type === 'payment' && (
              <span className="badge badge-warning" style={{ marginRight: '8px' }}>
                إجمالي: {results.totalAmount} ج.م
              </span>
            )}
          </h4>
          {results.results?.map(r => (
            <div key={r._id} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.name}</span>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {r.votersCount} صوت {results.type === 'payment' && `• ${r.optionTotal} ج.م`}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {r.voters?.map(v => (
                  <span key={v._id} className="badge badge-secondary" style={{ fontSize: '0.72rem' }}>
                    {v.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SurveyCard;
